import React, { ReactElement } from 'react'
import { generatePath } from 'react-router-dom'
import { RouteObject } from 'react-router'

import AddGroupManagersToGroupPage, {
  AddGroupManagersToGroupPageBreadcrumb,
} from './pages/AddGroupManagersToGroupPage/AddGroupManagersToGroupPage'
import AddGroupMembershipsToUserPage, {
  AddGroupMembershipsToUserPageBreadcrumb,
} from './pages/AddGroupMembershipsToUserPage/AddGroupMembershipsToUserPage'
import AddGroupMembershipsToUsersPage, {
  AddGroupMembershipsToUsersPageBreadcrumb,
} from './pages/AddGroupMembershipsToUsersPage/AddGroupMembershipsToUsersPage'
import AddLearningItemsToUserPage, {
  AddLearningItemsToUserPageBreadcrumb,
} from './pages/AddLearningItemsToUserPage/AddLearningItemsToUserPage'
import AddLearningItemsToUsersPage, {
  AddlearningItemsToUsersPageBreadcrumb,
} from './pages/AddLearningItemsToUsersPage/AddLearningItemsToUsersPage'
import AddMembersToGroupPage, {
  AddMembersToGroupPageBreadcrumb,
} from './pages/AddMembersToGroupPage/AddMembersToGroupPage'
import CourseCommentPage, { CourseCommentPageBreadcrumb } from './pages/CourseCommentPage/CourseCommentPage'
import CreateLearningItemPage, {
  CreateLearningItemPageBreadcrumb,
} from './pages/CreateLearningItemPage/CreateLearningItemPage'
import CreateUserPage, { CreateUserPageBreadcrumb } from './pages/CreateUserPage/CreateUserPage'
import EditLearningItemPage, {
  // EditCoursePageNew,
  EditLearningItemPageBreadcrumb,
} from './pages/EditLearningItemPage/EditLearningItemPage'
import GroupCommentPage, { GroupCommentPageBreadcrumb } from './pages/GroupCommentPage/GroupCommentPage'
import InsightsPage, { InsightsPageBreadcrumb } from './pages/InsightsPage/InsightsPage'
import CreateGroupLearningAssignmentsPage, {
  CreateGroupLearningAssignmentsPageBreadcrumb,
} from './pages/CreateGroupLearningAssignmentsPage/CreateGroupLearningAssignmentsPage'
import EditGroupLearningAssignmentPage, {
  EditGroupLearningAssignmentPageBreadcrumb,
} from './pages/EditGroupLearningAssignmentPage/EditGroupLearningAssignmentPage'
import EditUserLearningAssignmentPage, {
  EditUserLearningAssignmentPageBreadcrumb,
} from './pages/EditUserLearningAssignmentPage/EditUserLearningAssignmentPage'
import CoursesPage, { CoursesPageBreadcrumb } from './pages/CoursesPage/CoursesPage'
import CoursePage, { CoursePageBreadcrumb } from './pages/CoursePage/CoursePage'
import CourseAssetPage, { CourseAssetPageBreadcrumb } from './pages/CourseAssetPage/CourseAssetPage'
import CoursePlayPage, { CoursePlayPageBreadcrumb } from './pages/CoursePlayPage/CoursePlayPage'
import CreateGroupPage, { CreateGroupPageBreadcrumb } from './pages/CreateGroupPage/CreateGroupPage'
import DashboardPage, { DashboardPageBreadcrumb } from './pages/DashboardPage/DashboardPage'
import ManageGroupPage, { ManageGroupPageBreadcrumb } from './pages/ManageGroupPage/ManageGroupPage'
import ManageGroupsPage, { ManageGroupsPageBreadcrumb } from './pages/ManageGroupsPage/ManageGroupsPage'
import ManagePage, { ManagePageBreadcrumb } from './pages/ManagePage/ManagePage'
import ManageUserPage, { ManageUserPageBreadcrumb } from './pages/ManageUserPage/ManageUserPage'
import ManageUsersPage, { ManageUsersPageBreadcrumb } from './pages/ManageUsersPage/ManageUsersPage'
import MarkLearningItemAsFinishedPage, {
  MarkLearningItemAsFinishedPageBreadcrumb,
} from './pages/MarkLearningItemAsFinishedPage/MarkLearningItemAsFinishedPage'
import NotificationCenterPage, {
  NotificationCenterPageBreadcrumb,
} from './pages/NotificationCenterPage/NotificationCenterPage'
import RemoveGroupMembershipsFromUsersPage, {
  RemoveGroupMembershipsFromUsersPageBreadcrumb,
} from './pages/RemoveGroupMembershipsFromUsersPage/RemoveGroupMembershipsFromUsersPage'
import TestPage, { TestPageBreadcrumb } from './pages/TestPage/TestPage'
import EventDateAttendeesListPage, {
  EventDateAttendeesListPageBreadcrumb,
} from './pages/EventDateAttendeesListPage/EventDateAttendeesListPage'
import CreateLearningPathPage, {
  CreateLearningPathPageBreadcrumb,
} from './pages/CreateLearningPath/CreateLearningPathPage'

export interface RouteHandle {
  routeName: string
  breadcrumbs: ReactElement[]
}

function prepareRoutes<
  DEFS extends {
    [k: string]: {
      path: string
      parent?: string
      renderParent?: string
      element: ReactElement
      breadcrumb: ReactElement
    }
  },
>(defs: DEFS) {
  // Determine route hierarchy
  for (const [key, def] of Object.entries(defs)) {
    const path = def.path ? def.path.split('/') : []
    while (path.length && !def.parent) {
      path.pop()
      def.parent = Object.entries(defs).find(([_, def]) => def.path === path.join('/'))?.[0]
    }
    if (def.parent && !defs[def.parent]) {
      throw new Error(`Parent route '${def.parent}' for route ${key} not found`)
    }
    if (def.renderParent && !defs[def.renderParent]) {
      throw new Error(`Render parent route '${def.parent}' for route ${key} not found`)
    }
  }

  type PATHOF<D extends { path: string }> = D extends { path: infer P } ? P : never

  // Build routes
  const routes: {
    [k in keyof DEFS]: { url: (params: Parameters<typeof generatePath<PATHOF<DEFS[k]>>>[1]) => string }
  } = {} as any
  for (const [key, def] of Object.entries(defs)) {
    routes[key as keyof DEFS] = {
      url: (params: Parameters<typeof generatePath<string>>[1]) => '/' + generatePath(def.path, params),
    }
  }

  // Build react router definition
  const rrDefs: { [k: string]: RouteObject } = {}
  for (const [key, def] of Object.entries(defs)) {
    rrDefs[key] = {
      element: def.element,
    }
    if (def.path === '') {
      rrDefs[key].index = true
    } else if (def.renderParent) {
      const parentPath = defs[def.renderParent].path
      if (!def.path.startsWith(parentPath) || def.path[parentPath.length] !== '/') {
        throw new Error(
          `Route ${key}'s path '${def.path}' does not extend that of it's render parent ${def.renderParent} ('${parentPath}')`
        )
      }
      rrDefs[key].path = def.path.slice(parentPath.length + 1)
    } else {
      rrDefs[key].path = def.path
    }
  }

  // Add breadcrumbs
  for (const [key, rrDef] of Object.entries(rrDefs)) {
    const breadcrumbs = []
    let def = defs[key]
    while (def) {
      breadcrumbs.unshift(React.cloneElement(def.breadcrumb))
      def = defs[def.parent!]
    }
    rrDef.handle = {
      breadcrumbs,
      routeName: key,
    } as RouteHandle
  }

  // Put child routes under their parents
  for (const [key, rrDef] of Object.entries(rrDefs)) {
    const renderParent = defs[key].renderParent
    if (renderParent) {
      rrDefs[renderParent].children = rrDefs[renderParent].children || []
      rrDefs[renderParent].children!.push(rrDef)
    }
  }

  // Create array for react router
  const reactRouterRoutes = Object.entries(rrDefs)
    .filter(([key]) => !defs[key].renderParent)
    .map(([_, rrDef]) => rrDef)

  return {
    routes,
    reactRouterRoutes,
  }
}

const defs = {
  dashboard: {
    path: '',
    element: <DashboardPage />,
    breadcrumb: <DashboardPageBreadcrumb />,
  },
  courses: {
    path: 'courses',
    element: <CoursesPage />,
    breadcrumb: <CoursesPageBreadcrumb />,
  },
  createScheduledCourse: {
    path: 'courses/create-scheduled-course',
    element: <CreateLearningItemPage kind="scheduledCourse" />,
    breadcrumb: <CreateLearningItemPageBreadcrumb />,
  },
  createOnDemandCourse: {
    path: 'courses/create-on-demand-course',
    element: <CreateLearningItemPage kind="onDemandCourse" />,
    breadcrumb: <CreateLearningItemPageBreadcrumb />,
  },
  createLearningPath: {
    path: 'courses/create-learning-path',
    element: <CreateLearningPathPage />,
    breadcrumb: <CreateLearningPathPageBreadcrumb />,
  },
  course: {
    path: 'courses/:uuid',
    element: <CoursePage />,
    breadcrumb: <CoursePageBreadcrumb />,
  },
  courseAsset: {
    path: 'courses/:uuid/assets/:assetUuid',
    element: <CourseAssetPage />,
    breadcrumb: <CourseAssetPageBreadcrumb />,
    renderParent: 'course',
  },
  courseComment: {
    path: 'courses/:uuid/comments/:commentUuid',
    element: <CourseCommentPage />,
    breadcrumb: <CourseCommentPageBreadcrumb />,
  },
  previewCourse: {
    path: 'courses/:uuid/preview',
    element: <CoursePlayPage preview />,
    breadcrumb: <CoursePlayPageBreadcrumb preview />,
  },
  playCourse: {
    path: 'courses/:uuid/play',
    element: <CoursePlayPage />,
    breadcrumb: <CoursePlayPageBreadcrumb />,
  },
  editCourse: {
    path: 'courses/:uuid/edit/:kind?',
    element: <EditLearningItemPage />,
    breadcrumb: <EditLearningItemPageBreadcrumb />,
  },
  manageCourseEventDateAttendees: {
    // fromPath = 1 if the page was opened from a learning path item, nothing otherwise
    path: 'courses/:uuid/events/:eventUuid/dates/:dateUuid/attendees/:fromPath?',
    element: <EventDateAttendeesListPage />,
    breadcrumb: <EventDateAttendeesListPageBreadcrumb />,
    renderParent: 'course',
  },
  insights: {
    path: 'insights',
    element: <InsightsPage />,
    breadcrumb: <InsightsPageBreadcrumb />,
  },
  manage: {
    path: 'manage',
    element: <ManagePage />,
    breadcrumb: <ManagePageBreadcrumb />,
  },
  manageUsers: {
    path: 'manage/users',
    element: <ManageUsersPage />,
    breadcrumb: <ManageUsersPageBreadcrumb />,
  },
  createUser: {
    path: 'manage/users/new',
    element: <CreateUserPage />,
    breadcrumb: <CreateUserPageBreadcrumb />,
    renderParent: 'manageUsers',
  },
  AddGroupMembershipsToUsers: {
    path: 'manage/users/add-group-memberships/:uuids',
    element: <AddGroupMembershipsToUsersPage />,
    breadcrumb: <AddGroupMembershipsToUsersPageBreadcrumb />,
    renderParent: 'manageUsers',
  },
  removeGroupMembershipsFromUsers: {
    path: 'manage/users/remove-group-memberships/:uuids',
    element: <RemoveGroupMembershipsFromUsersPage />,
    breadcrumb: <RemoveGroupMembershipsFromUsersPageBreadcrumb />,
    renderParent: 'manageUsers',
  },
  addLearningItemsToUsers: {
    path: 'manage/users/add-learning-items/:uuids',
    element: <AddLearningItemsToUsersPage />,
    breadcrumb: <AddlearningItemsToUsersPageBreadcrumb />,
    renderParent: 'manageUsers',
  },
  manageUser: {
    path: 'manage/users/:uuid',
    element: <ManageUserPage />,
    breadcrumb: <ManageUserPageBreadcrumb />,
  },
  AddGroupMembershipsToUser: {
    path: 'manage/users/:uuid/add-group-memberships',
    element: <AddGroupMembershipsToUserPage />,
    breadcrumb: <AddGroupMembershipsToUserPageBreadcrumb />,
    renderParent: 'manageUser',
  },
  addLearningItemsToUser: {
    path: 'manage/users/:uuid/add-learning-items/:pathsOrCourses?',
    element: <AddLearningItemsToUserPage />,
    breadcrumb: <AddLearningItemsToUserPageBreadcrumb />,
    renderParent: 'manageUser',
  },
  editUserLearningAssignment: {
    path: 'manage/users/:uuid/learning-assignment/:learningItemUuid',
    element: <EditUserLearningAssignmentPage />,
    breadcrumb: <EditUserLearningAssignmentPageBreadcrumb />,
    renderParent: 'manageUser',
  },
  markLearningItemAsFinished: {
    path: 'manage/users/:uuid/learning-item/:learningItemUuid/mark-as-finished',
    element: <MarkLearningItemAsFinishedPage />,
    breadcrumb: <MarkLearningItemAsFinishedPageBreadcrumb />,
    renderParent: 'manageUser',
  },
  manageGroups: {
    path: 'manage/groups',
    element: <ManageGroupsPage />,
    breadcrumb: <ManageGroupsPageBreadcrumb />,
  },
  createGroup: {
    path: 'manage/groups/new',
    element: <CreateGroupPage />,
    breadcrumb: <CreateGroupPageBreadcrumb />,
    renderParent: 'manageGroups',
  },
  manageGroup: {
    path: 'manage/groups/:uuid',
    element: <ManageGroupPage />,
    breadcrumb: <ManageGroupPageBreadcrumb />,
  },
  addMembersToGroups: {
    path: 'manage/groups/:uuid/add-members',
    element: <AddMembersToGroupPage />,
    breadcrumb: <AddMembersToGroupPageBreadcrumb />,
    renderParent: 'manageGroup',
  },
  addGroupManagersToGroups: {
    path: 'manage/groups/:uuid/add-group-managers',
    element: <AddGroupManagersToGroupPage />,
    breadcrumb: <AddGroupManagersToGroupPageBreadcrumb />,
    renderParent: 'manageGroup',
  },
  createGroupLearningAssignments: {
    path: 'manage/groups/:uuid/create-learning-assignments/:pathsOrCourses?',
    element: <CreateGroupLearningAssignmentsPage />,
    breadcrumb: <CreateGroupLearningAssignmentsPageBreadcrumb />,
    renderParent: 'manageGroup',
  },
  editGroupLearningAssignment: {
    path: 'manage/groups/:uuid/learning-assignment/:learningItemUuid',
    element: <EditGroupLearningAssignmentPage />,
    breadcrumb: <EditGroupLearningAssignmentPageBreadcrumb />,
    renderParent: 'manageGroup',
  },
  groupComment: {
    path: 'manage/groups/:uuid/comments/:commentUuid',
    element: <GroupCommentPage />,
    breadcrumb: <GroupCommentPageBreadcrumb />,
  },
  notifications: {
    path: 'notifications',
    element: <NotificationCenterPage />,
    breadcrumb: <NotificationCenterPageBreadcrumb />,
  },
  test: {
    path: 'test/:which?',
    element: <TestPage />,
    breadcrumb: <TestPageBreadcrumb />,
  },
} as const

const { routes, reactRouterRoutes } = prepareRoutes(defs)

export default routes

export { reactRouterRoutes }
